import { create } from 'zustand';
import { applyTheme, type ThemeName } from '@/themes/theme-config';
import { configDb } from '@/persistence/config-db';

/**
 * Identifies a sidebar modal that participates in the single-modal
 * rule (Phase 10 step 8.3). At most one of these can be open at a
 * time; opening a new one auto-closes any other.
 *
 * Per-widget panels (e.g. WidgetSettingsRightPanel) and inline picker
 * popovers stay outside this set — they're scoped to a specific
 * widget instance and don't conflict with sidebar flows.
 */
export type SidebarModalKey =
  | 'column-settings'
  | 'value-groupings'
  | 'column-match'
  | 'import-review';

/**
 * Sidebar nav item identifier — drives which sub-panel is open beside
 * the 64px rail. Privacy is intentionally not in this set: it triggers
 * a modal (PrivacyModal) rather than opening a sub-panel.
 */
export type NavId =
  | 'data'
  | 'widget'
  | 'filter'
  | 'dataconf'
  | 'dashboard'
  | 'appearance';

type UiState = {
  /** Active theme. */
  theme: ThemeName;
  /** Whether the privacy detail modal is open. */
  privacyModalOpen: boolean;
  /**
   * Which sidebar modal is currently open, or `null` for none.
   * Setting this implicitly closes any other modal — that's the
   * whole point of routing sidebar modals through a single slot.
   */
  activeModal: SidebarModalKey | null;
  /**
   * Which sidebar sub-panel is open (next to the 64px rail), or
   * `null` for none. Mutually exclusive with `widgetSettingsTarget`:
   * opening one closes the other.
   */
  activeNav: NavId | null;
  /**
   * Widget id whose settings are open in the right panel, or `null`
   * for none. Mutually exclusive with `activeNav`.
   */
  widgetSettingsTarget: string | null;

  /** Switch theme — applies CSS + persists to IndexedDB. */
  setTheme: (name: ThemeName) => void;
  /** Toggle privacy detail modal. */
  setPrivacyModalOpen: (open: boolean) => void;
  /** Open the given modal, replacing whichever was open. Pass `null` to close. */
  setActiveModal: (next: SidebarModalKey | null) => void;
  /** Open the given sub-panel. Closes the right panel as a side effect. */
  setActiveNav: (next: NavId | null) => void;
  /** Open the right panel for the given widget. Closes the sub-panel as a side effect. */
  setWidgetSettingsTarget: (widgetId: string | null) => void;
};

export const useUiStore = create<UiState>((set) => ({
  theme: 'light',
  privacyModalOpen: false,
  activeModal: null,
  activeNav: null,
  widgetSettingsTarget: null,

  setTheme: (name) => {
    applyTheme(name);
    set({ theme: name });
    // Fire-and-forget persistence — non-blocking.
    void configDb.preferences.put({ key: 'theme', value: name });
  },

  setPrivacyModalOpen: (open) => set({ privacyModalOpen: open }),
  setActiveModal: (next) => set({ activeModal: next }),
  setActiveNav: (next) => set({ activeNav: next, widgetSettingsTarget: null }),
  setWidgetSettingsTarget: (id) => set({ widgetSettingsTarget: id, activeNav: null }),
}));

/**
 * Bootstrap the UI store from persisted preferences.
 * Call once at app startup, before first render if possible.
 */
export async function initUiStore(): Promise<void> {
  try {
    const pref = await configDb.preferences.get('theme');
    if (pref) {
      const name = pref.value as ThemeName;
      applyTheme(name);
      useUiStore.setState({ theme: name });
    }
  } catch {
    // IndexedDB unavailable — fall through to light theme default.
  }
}
